// Open-shift request row shape and the SELF-side refusal allowlists, frozen by the Wave 6A
// DB contract and shipped in migration 20260808120000_schedule_color_open_fill.
//
// All six request RPCs return the SAME row shape, so the mapper lives here once: console
// (6B desk), guard web (6C), and mobile (6D) read it rather than retyping column names.
// Every sentence below is copied verbatim from a `raise exception` in that migration.
//
// Split by ACTOR, deliberately: this file carries the paths a guard can walk (claim,
// request, cancel, list-mine). The desk-only refusals (approve / reject / list-account)
// stay in the console, because no guard client should be able to render them.
//
// FOLLOW-UP (not done here, to keep Wave 6C scoped to guard web): the console still has its
// own byte-identical copy of the mapper in apps/console/src/data/open-shift-requests.ts.
// That file should re-export `OpenShiftRequest` / `mapOpenShiftRequest` / `mapOpenShiftRequests`
// from here so one frozen row shape has exactly one implementation.

import {
  asColorToken,
  asOpenFillMode,
  asOpenShiftRequestStatus,
  type ColorToken,
  type OpenFillMode,
  type OpenShiftRequestStatus,
} from "./schedule-color.ts";

/** The 6A request row shape, identical across every request RPC. */
export interface OpenShiftRequest {
  readonly requestId: string;
  readonly shiftSlotId: string | null;
  readonly shiftTemplateId: string | null;
  readonly colorToken: ColorToken;
  readonly openFillMode: OpenFillMode;
  readonly siteName: string | null;
  readonly routeName: string | null;
  readonly slotStartsAt: string | null;
  readonly slotEndsAt: string | null;
  readonly status: OpenShiftRequestStatus;
  readonly requestNote: string | null;
  readonly requesterDisplayName: string | null;
  readonly rejectionReason: string | null;
  readonly routeAssignmentId: string | null;
  readonly createdAt: string | null;
  readonly reviewedAt: string | null;
}

function asText(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

/** A row without an id or a recognized status is dropped, not guessed. */
export function mapOpenShiftRequest(raw: unknown): OpenShiftRequest | null {
  if (raw === null || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const requestId = asText(row.request_id);
  const status = asOpenShiftRequestStatus(row.status);
  if (requestId === null || status === null) return null;
  return {
    requestId,
    shiftSlotId: asText(row.shift_slot_id),
    shiftTemplateId: asText(row.shift_template_id),
    colorToken: asColorToken(row.color_token),
    openFillMode: asOpenFillMode(row.open_fill_mode),
    // The contract returns '' only when a name is truly unreachable; treat it as unknown
    // so the row renders the route alone rather than a gap.
    siteName: asText(row.site_name),
    routeName: asText(row.route_name),
    slotStartsAt: asText(row.slot_starts_at),
    slotEndsAt: asText(row.slot_ends_at),
    status,
    requestNote: asText(row.request_note),
    requesterDisplayName: asText(row.requester_display_name),
    rejectionReason: asText(row.rejection_reason),
    routeAssignmentId: asText(row.route_assignment_id),
    createdAt: asText(row.created_at),
    reviewedAt: asText(row.reviewed_at),
  };
}

export function mapOpenShiftRequests(data: unknown): OpenShiftRequest[] {
  const rows = Array.isArray(data) ? data : [];
  const mapped: OpenShiftRequest[] = [];
  for (const raw of rows) {
    const request = mapOpenShiftRequest(raw);
    if (request !== null) mapped.push(request);
  }
  return mapped;
}

// --- Refusal allowlists (verbatim; guard-reachable paths only) -------------------------------

/**
 * Every non-internal sentence `claim_open_shift_slot` can raise. Each one tells the guard
 * something different about their own eligibility, so collapsing them all into one "could
 * not claim" would hide the only fact that changes what they do next.
 */
export const CLAIM_OPEN_SHIFT_FAILURES: ReadonlySet<string> = new Set([
  "Authentication is required to claim an open shift.",
  "Account is required to claim an open shift.",
  "Shift slot is required to claim an open shift.",
  "Active guard membership is required to claim an open shift.",
  "Caller is not authorized to claim open shifts for this account.",
  "Open shift was not found for this account.",
  "Open shift for this template requires a request. Use request instead of claim.",
  "Only published open shifts can be claimed.",
  "Past open shifts cannot be claimed.",
  "Open shift is not claimable until its route, site, and client are active and live.",
  "Open shift is already fully claimed.",
  "Guard already has an overlapping scheduled or active route assignment.",
]);

/** Every non-internal sentence request / cancel / list-mine can raise. */
export const SELF_OPEN_SHIFT_REQUEST_FAILURES: ReadonlySet<string> = new Set([
  "Authentication is required to request open shifts.",
  "Account id is required.",
  "Active account membership is required.",
  "Caller is not authorized to request open shifts for this account.",
  "Open shift request note must be 500 characters or fewer.",
  "Open shift request was not found or is not available.",
  "Open shift for this template is claim-only. Use claim instead of request.",
  "Open shift has no remaining seats.",
  "An open shift request is already pending for this slot.",
  "Open shift request is not pending.",
]);

/**
 * Refusals that mean the board the guard is looking at is out of date. Surfacing the
 * sentence is not enough on its own - the row that produced it has to be refetched, or the
 * guard taps the same wrong button again.
 *
 * The two mode-mismatch sentences are the important ones: the desk flipped the template
 * between page load and tap, and a refetch is exactly what turns Claim into Request.
 */
export const STALE_BOARD_FAILURES: ReadonlySet<string> = new Set([
  "Open shift for this template requires a request. Use request instead of claim.",
  "Open shift for this template is claim-only. Use claim instead of request.",
  "Open shift is already fully claimed.",
  "Open shift has no remaining seats.",
  "An open shift request is already pending for this slot.",
  "Open shift request was not found or is not available.",
  "Open shift request is not pending.",
  "Only published open shifts can be claimed.",
  "Past open shifts cannot be claimed.",
]);

/**
 * Internal guards. These name a caller-binding failure, never anything a guard can act on,
 * and must stay out of every allowlist above.
 */
export const OPEN_SHIFT_INTERNAL_GUARDS: readonly string[] = [
  "Open shift claim caller mismatch.",
  "Open shift request caller mismatch.",
  "Open shift request manager caller mismatch.",
];

/** Picks the verbatim sentence when it is allowlisted, else the caller's safe fallback. */
export function resolveOpenShiftFailure(
  message: unknown,
  allowlist: ReadonlySet<string>,
  safeFallback: string,
): { readonly reason: string; readonly stale: boolean } {
  const text = typeof message === "string" ? message : "";
  return allowlist.has(text)
    ? { reason: text, stale: STALE_BOARD_FAILURES.has(text) }
    : { reason: safeFallback, stale: false };
}
